import {
  Box,
  Step,
  Stack,
  Button,
  Divider,
  Stepper,
  TextField,
  StepLabel,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowForwardRounded } from "@mui/icons-material";
import { FC, ChangeEvent, useEffect, useRef, useState } from "react";
import { useForm, FormProvider, useFormContext } from "react-hook-form";

import { ActionModal } from "@/shared/base/components/ActionModal/ActionModal";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { PhoneNumberInput } from "@/shared/base/components/FormComponents/PhoneNumberInput";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { BorderLinearProgress } from "@/shared/base/components/BorderLinearProgress/BorderLinearProgress";

import { createState } from "@/global/store/zustand_store";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import {
  useRepsProduct,
  type ConfirmAccountProps,
  type ValidateAccountIdProps,
} from "../useRepsProduct";
import type { RecipientUser } from "@/types";

type RepsProductModalProps = {
  activeStep: number;
  handleBackStep: () => void;
  recipientUser: RecipientUser;
  getCustomerAccountError?: string;
  handleResetErrorMessage: () => void;
  handleFailedStep: (step: number) => boolean;
  handleConfirmAccount: (data: ConfirmAccountProps) => void;
  handleValidateAccountId: (data: ValidateAccountIdProps) => Promise<void>;
};

const steps = ["Step 1", "Step 2"];

export const useRepsProductModal = createState({
  isOpen: false,
  hasModalOpened: false,
});

export const useRepsProductWarningModal = createState({
  isOpen: false,
  hasModalOpened: false,
});

export const RepsProductModal: FC<RepsProductModalProps> = ({
  activeStep,
  recipientUser,
  handleBackStep,
  handleFailedStep,
  handleConfirmAccount,
  getCustomerAccountError,
  handleValidateAccountId,
  handleResetErrorMessage,
}) => {
  const { sm } = useCssMediaQuery("down");
  const modalState = useRepsProductModal();

  const methods = useForm<ValidateAccountIdProps>();
  const { handleSubmit, formState } = methods;

  const confirmMethods = useForm<ConfirmAccountProps>();
  const { handleSubmit: handleConfirmSubmit, formState: confirmFormState } =
    confirmMethods;

  return (
    <ActionModal
      hideCloseIcon
      isOpen={modalState.isOpen}
      sx={{
        mx: 2,
      }}
      renderHeader={() => (
        <Stepper
          activeStep={activeStep}
          sx={{
            p: 2,
          }}
        >
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};

            const labelProps: {
              error?: boolean;
            } = {};

            if (
              (!!Object.keys(formState.errors).length &&
                handleFailedStep(index)) ||
              (getCustomerAccountError && handleFailedStep(index))
            ) {
              labelProps.error = true;
            }

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      )}
      renderBody={() => (
        <Box>
          {activeStep === 0 ? (
            <form onSubmit={handleSubmit(handleValidateAccountId)}>
              <FormProvider {...methods}>
                <Box p={sm ? 2 : 3}>
                  <Stack spacing={1}>
                    <Typography fontSize={"1rem"}>
                      Please enter the Account ID of the user your are
                      completing this transaction on behalf of:
                    </Typography>
                    <AccountID
                      handleResetErrorMessage={handleResetErrorMessage}
                    />
                    {getCustomerAccountError ? (
                      <Typography
                        color="error.main"
                        ml="8px"
                        mt="0!important"
                        fontSize="12px"
                        minHeight="18px"
                      >
                        {getCustomerAccountError}
                      </Typography>
                    ) : null}

                    {formState.isSubmitting ? (
                      <Box mt={2}>
                        <BorderLinearProgress variant="indeterminate" />
                      </Box>
                    ) : null}
                  </Stack>
                </Box>

                <Divider />

                <Stack
                  p={2}
                  gap={2}
                  width={"100%"}
                  direction={"row"}
                  justifyContent={"flex-end"}
                >
                  <LoadingButton
                    size={"large"}
                    type={"submit"}
                    loading={formState.isSubmitting}
                    variant={"contained"}
                    sx={{
                      width: sm ? "fit-content" : "180px",
                      fontWeight: 600,
                    }}
                  >
                    Continue
                  </LoadingButton>
                </Stack>
              </FormProvider>
            </form>
          ) : null}

          {activeStep === 1 ? (
            <form onSubmit={handleConfirmSubmit(handleConfirmAccount)}>
              <FormProvider {...confirmMethods}>
                <Box p={sm ? 2 : 3} pt={1}>
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography fontSize={"1rem"}>Account ID:</Typography>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        placeholder={String(recipientUser?.accountId)}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography fontSize={"1rem"}>Email:</Typography>
                      <TextField
                        disabled
                        fullWidth
                        size="small"
                        placeholder={recipientUser?.email}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography fontSize={"1rem"}>Phone Number:</Typography>
                      <PhoneNumberInput
                        options={{ required: false }}
                        defaultValue={recipientUser?.phoneNumber}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Divider />

                <Stack
                  p={2}
                  gap={2}
                  width={"100%"}
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <ModalActionButton
                    variant="secondary"
                    size="large"
                    onClick={handleBackStep}
                    style={{
                      borderRadius: 99,
                    }}
                  >
                    Back
                  </ModalActionButton>

                  <LoadingButton
                    size={"large"}
                    type={"submit"}
                    loading={confirmFormState.isSubmitting}
                    variant={"contained"}
                    sx={{
                      width: sm ? "fit-content" : "180px",
                      fontWeight: 600,
                    }}
                  >
                    Confirm
                  </LoadingButton>
                </Stack>
              </FormProvider>
            </form>
          ) : null}
        </Box>
      )}
    />
  );
};

type AccountIDProps = {
  handleResetErrorMessage: () => void;
};

export function AccountID({ handleResetErrorMessage }: AccountIDProps) {
  const fieldName = "accountId";
  const { trigger, setValue, register, formState } = useFormContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const error = formState.errors[fieldName]?.message as string;

  function validateAccountID(value: string) {
    handleResetErrorMessage();
    return /^[0-9]*$/.test(value) || "Account ID must be a number";
  }

  function handleAccountID(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value.replace(/\D/g, ""); // remove non-numeric characters
    setValue(fieldName, value);
    trigger(fieldName);
  }

  useEffect(() => {
    function handleClearInput() {
      if (inputRef.current?.value === "") handleResetErrorMessage();
    }

    const inputElement = inputRef.current;
    inputElement?.addEventListener("input", handleClearInput);
    return () => {
      inputElement?.removeEventListener("input", handleClearInput);
    };
  }, [handleResetErrorMessage]);

  return (
    <Stack flexGrow={1}>
      <TextField
        {...register(fieldName, {
          validate: validateAccountID,
          required: "Account ID is Required",
        })}
        fullWidth
        size="small"
        onChange={handleAccountID}
        placeholder={"Account ID"}
        inputRef={inputRef}
      />

      <Typography color="error.main" ml="8px" fontSize="12px" minHeight="18px">
        {!!error && error}
      </Typography>
    </Stack>
  );
}

export const RepsProductWarningModal: FC = () => {
  const { states } = useRepsProduct();
  const { sm } = useCssMediaQuery("down");
  const modalState = useRepsProductWarningModal();

  const handleConfirm = async () => {
    modalState.set({ isOpen: false });
    const cartConfirmBtn = document.getElementById(
      "confirm-game-selection-btn"
    );
    cartConfirmBtn?.click();
  };

  if (!states.isRepProduct) return null;

  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      hideSecondaryButton
      title="Representative Product"
      isOpen={modalState.isOpen}
      onClose={() => modalState.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => (
        <Box>
          <Box p={sm ? 2 : 3}>
            <Box>
              <Typography variant="body1">
                Continue to log in with your TM account linked to your team
                email.
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      renderFooter={() => (
        <Stack
          py={2}
          px={2}
          gap={2}
          width={"100%"}
          justifyContent={"flex-end"}
          direction={"row"}
        >
          <Button
            size={"large"}
            variant={"contained"}
            endIcon={<ArrowForwardRounded />}
            onClick={handleConfirm}
            sx={{
              width: sm ? "fit-content" : "180px",
              fontWeight: 600,
            }}
          >
            Continue
          </Button>
        </Stack>
      )}
    />
  );
};
